import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import Sidebar from '@/components/Sidebar';
import { useTranslation } from '@/hooks/useTranslation';
import {
  Users,
  Search,
  Eye,
  Phone,
  MapPin,
  Calendar,
  ShoppingBag,
  Menu,
  RefreshCw,
  UserPlus,
  Repeat,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  Truck,
  CreditCard,
  FileText,
  ArrowUpDown,
  User,
  SlidersHorizontal,
  MessageSquare,
  Check,
  Inbox
} from 'lucide-react';
import Pagination from '@/components/Pagination';

const STATUS_CONFIG = {
  'Pending': { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  'Paid': { color: 'bg-indigo-100 text-blue-800', icon: CreditCard },
  'Shipped': { color: 'bg-purple-100 text-purple-800', icon: Truck },
  'Completed': { color: 'bg-green-100 text-green-800', icon: CheckCircle },
  'Cancelled': { color: 'bg-red-100 text-red-800', icon: XCircle }
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0
  }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Jakarta'
  });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Jakarta'
  });
};

// Sort options for the drawer
const SORT_OPTIONS = [
  { value: 'last_order-desc', label: 'Terakhir Order (Terbaru)' },
  { value: 'last_order-asc', label: 'Terakhir Order (Terlama)' },
  { value: 'total_orders-desc', label: 'Total Order (Terbanyak)' },
  { value: 'total_orders-asc', label: 'Total Order (Tersedikit)' },
  { value: 'total_spent-desc', label: 'Total Belanja (Terbesar)' },
  { value: 'total_spent-asc', label: 'Total Belanja (Terkecil)' },
  { value: 'name-asc', label: 'Nama A-Z' },
  { value: 'name-desc', label: 'Nama Z-A' },
];

// Customer Detail Modal
// Customer Detail Modal - Bottom Sheet Style
const CustomerDetailModal = ({ customer, isOpen, onClose }) => {
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [addressExpanded, setAddressExpanded] = useState(false);
  const { user, token } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    if (customer) {
      fetchNotes();
      setAddressExpanded(false);
    }
  }, [customer]);

  const fetchNotes = async () => {
    try {
      const response = await axios.get(
        `${API}/customers/${encodeURIComponent(customer.customer_phone)}/notes?user_id=${user.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotes(response.data.notes || '');
    } catch (error) {
      console.error('Error fetching notes:', error);
    }
  };

  const saveNotes = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/customers/${encodeURIComponent(customer.customer_phone)}/notes?user_id=${user.id}`,
        { notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t('customers.notesSaved') || 'Catatan tersimpan');
    } catch (error) {
      toast.error(t('customers.notesSaveFailed') || 'Gagal menyimpan catatan');
    } finally {
      setSaving(false);
    }
  };

  if (!customer) return null;

  const hasLongAddress = customer.address && customer.address !== '-' && customer.address.length > 60;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent hideClose className="sm:max-w-lg max-h-[85vh] p-0 gap-0 overflow-hidden bg-white dark:bg-[#0A0A0A] rounded-t-2xl sm:rounded-xl" data-testid="customer-detail-modal">
        {/* Drag Handle (mobile) */}
        <div className="flex justify-center pt-2 pb-0 sm:hidden">
          <div className="w-10 h-1 rounded-full bg-slate-300 dark:bg-zinc-700" />
        </div>

        {/* Sticky Header */}
        <div className="sticky top-0 z-10 bg-white dark:bg-[#0A0A0A] px-4 pt-3 pb-3 border-b border-slate-100 dark:border-zinc-800/60">
          <div className="flex items-center justify-between">
            <DialogHeader className="p-0 flex-1">
              <DialogTitle className="flex items-center gap-2 text-base font-semibold">
                <User className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                {t('customers.customerDetail')}
              </DialogTitle>
            </DialogHeader>
            <button
              onClick={() => onClose(false)}
              className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-slate-100 dark:hover:bg-zinc-900 transition-colors shrink-0 dark:bg-zinc-800"
              data-testid="close-customer-detail-btn"
              aria-label="Close"
            >
              <XCircle className="w-5 h-5 text-slate-500 dark:text-zinc-400" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto flex-1 px-4 pb-4" style={{ maxHeight: 'calc(85vh - 60px)' }}>
          <div className="space-y-4 pt-4">

            {/* Customer Info - Compact */}
            <div className="flex items-center gap-3" data-testid="customer-info-section">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-lg font-bold shrink-0">
                {customer.customer_name?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-base font-bold text-slate-900 dark:text-zinc-50 truncate">
                  {customer.customer_name || 'Pelanggan'}
                </h3>
                <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-500 dark:text-zinc-400">
                  <span className="flex items-center gap-1">
                    <Phone className="w-3 h-3" />
                    {customer.customer_phone || '-'}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatDate(customer.first_order)}
                  </span>
                </div>
              </div>
            </div>

            {/* Address - Collapsible */}
            {customer.address && customer.address !== '-' && (
              <div className="flex items-start gap-2 px-3 py-2 bg-slate-50 dark:bg-zinc-900/50 rounded-xl" data-testid="customer-address">
                <MapPin className="w-3.5 h-3.5 text-slate-500 mt-0.5 shrink-0 dark:text-zinc-400" />
                <div className="min-w-0 flex-1">
                  <p className={`text-xs text-slate-600 dark:text-zinc-400 leading-relaxed ${!addressExpanded && hasLongAddress ? 'line-clamp-1 sm:line-clamp-none' : ''}`}>
                    {customer.address}
                  </p>
                  {hasLongAddress && (
                    <button
                      onClick={() => setAddressExpanded(!addressExpanded)}
                      className="text-[10px] text-indigo-600 dark:text-indigo-400 font-medium mt-0.5 sm:hidden"
                      data-testid="toggle-address-btn"
                    >
                      {addressExpanded ? 'Sembunyikan' : 'Lihat selengkapnya'}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Stats - Hero + 2 Column */}
            <div className="space-y-2" data-testid="customer-stats">
              <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-3 text-center border border-emerald-100 dark:border-emerald-800/40">
                <p className="text-[10px] text-emerald-600 dark:text-emerald-500 uppercase tracking-wide mb-0.5">Total Belanja</p>
                <p className="text-xl font-bold text-emerald-700 dark:text-emerald-400" data-testid="stat-total-spent">{formatCurrency(customer.total_spent)}</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-slate-50 dark:bg-zinc-900 rounded-xl p-3 text-center flex flex-col justify-between">
                  <p className="text-lg font-bold text-slate-900 dark:text-zinc-50 leading-7" data-testid="stat-total-orders">{customer.total_orders}</p>
                  <p className="text-[10px] text-slate-500 dark:text-zinc-400 mt-0.5 uppercase tracking-wide">Pesanan</p>
                </div>
                <div className="bg-slate-50 dark:bg-zinc-900 rounded-xl p-3 text-center flex flex-col justify-between">
                  <p className="text-sm font-bold text-slate-900 dark:text-zinc-50 leading-7" data-testid="stat-avg-order">{formatCurrency(customer.avg_order_value)}</p>
                  <p className="text-[10px] text-slate-500 dark:text-zinc-400 mt-0.5 uppercase tracking-wide">Rata-rata</p>
                </div>
              </div>
            </div>

            {/* Notes - Clean Input Area */}
            <div className="space-y-2" data-testid="customer-notes-section">
              <Label className="text-xs font-semibold flex items-center gap-1.5 text-slate-700 dark:text-zinc-300">
                <FileText className="w-3.5 h-3.5" />
                Catatan Internal
              </Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Tambahkan catatan tentang pelanggan ini..."
                rows={2}
                className="text-sm bg-white dark:bg-zinc-900 border-slate-200 dark:border-zinc-800 rounded-xl resize-none"
                data-testid="customer-notes-input"
              />
              <Button
                onClick={saveNotes}
                disabled={saving}
                className="w-full h-9 gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium rounded-xl"
                data-testid="save-notes-btn"
              >
                {saving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                {saving ? 'Menyimpan...' : 'Simpan Catatan'}
              </Button>
            </div>

            {/* Order History */}
            <div data-testid="order-history-section">
              <h4 className="text-xs font-semibold flex items-center gap-1.5 text-slate-700 dark:text-zinc-300 mb-2 uppercase tracking-wide">
                <ShoppingBag className="w-3.5 h-3.5" />
                Riwayat Pesanan ({customer.total_orders})
              </h4>
              <div className="space-y-2">
                {customer.orders?.map((order) => {
                  const StatusIcon = STATUS_CONFIG[order.status]?.icon || Clock;
                  return (
                    <div
                      key={order.id}
                      className="bg-white dark:bg-zinc-900 rounded-xl p-3 border border-slate-100 dark:border-zinc-800/60"
                      data-testid={`order-item-${order.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-slate-900 dark:text-zinc-50 truncate">
                            {order.product || '-'}
                          </p>
                          <p className="text-[11px] text-slate-500 dark:text-zinc-400 mt-0.5">
                            {order.variant && `${order.variant} · `}Qty: {order.quantity || 1}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-sm font-semibold text-slate-900 dark:text-zinc-50">
                            {formatCurrency(order.total)}
                          </p>
                          <Badge className={`${STATUS_CONFIG[order.status]?.color} text-[10px] mt-1 px-1.5 py-0 h-5`}>
                            <StatusIcon className="w-2.5 h-2.5 mr-0.5" />
                            {order.status}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 mt-2 pt-2 border-t border-slate-100 dark:border-zinc-800/40 text-[10px] text-slate-500 dark:text-zinc-500">
                        <span>{formatDateTime(order.created_at)}</span>
                        {order.tracking_number && (
                          <span className="flex items-center gap-0.5">
                            <Truck className="w-2.5 h-2.5" />
                            {order.courier}: {order.tracking_number}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
                {(!customer.orders || customer.orders.length === 0) && (
                  <p className="text-xs text-slate-500 dark:text-zinc-500 text-center py-4">Belum ada riwayat pesanan</p>
                )}
              </div>
            </div>

          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main Component
export default function CustomersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('last_order');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [optionsDrawerOpen, setOptionsDrawerOpen] = useState(false);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 25,
    total: 0,
    total_pages: 1,
    has_prev: false,
    has_next: false
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchCustomers = async (page = pagination.page) => {
    try {
      const params = new URLSearchParams({ 
        user_id: user.id,
        sort_by: sortBy,
        sort_order: sortOrder,
        page: page.toString(),
        per_page: pagination.per_page.toString()
      });
      if (searchQuery) params.append('search', searchQuery);

      const response = await axios.get(`${API}/customers?${params}`, { headers });
      setCustomers(response.data.customers || []);
      if (response.data.pagination) {
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error(t('common.error'));
    }
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= pagination.total_pages) {
      fetchCustomers(page);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/customers/stats?user_id=${user.id}`, { headers });
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    if (user?.id) {
      Promise.all([fetchCustomers(1), fetchStats()]).finally(() => setLoading(false));
    }
  }, [user]);

  useEffect(() => {
    if (user?.id) {
      fetchCustomers(1);
    }
  }, [sortBy, sortOrder, searchQuery]);

  const openCustomerDetail = async (customer) => {
    try {
      const response = await axios.get(
        `${API}/customers/${encodeURIComponent(customer.customer_phone)}?user_id=${user.id}`,
        { headers }
      );
      setSelectedCustomer(response.data.customer);
      setShowDetail(true);
    } catch (error) {
      toast.error('Gagal memuat detail pelanggan');
    }
  };

  const currentSortValue = `${sortBy}-${sortOrder}`;
  const currentSortLabel = SORT_OPTIONS.find(o => o.value === currentSortValue)?.label || 'Terakhir Order';
  const hasCustomers = customers.length > 0;
  const hasAnyData = stats && stats.total_customers > 0;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0A0A0A]">
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      <div className="lg:ml-64">
        {/* Header — compact on mobile */}
        <header className="sticky top-0 backdrop-blur-xl bg-white/85 dark:bg-[#0A0A0A]/85 border-b border-slate-200 dark:border-zinc-800/80 z-30">
          <div className="px-3 sm:px-6 py-2.5 sm:py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3">
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="lg:hidden p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-zinc-800 dark:bg-zinc-800"
                  data-testid="mobile-menu-btn"
                >
                  <Menu className="w-5 h-5" />
                </button>
                <h1 className="text-lg sm:text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50" style={{ fontFamily: 'Manrope' }} data-testid="page-title">
                  {t('customers.title')}
                </h1>
              </div>
              {/* Desktop subtitle */}
              <p className="text-sm text-slate-500 hidden sm:block dark:text-zinc-400">{t('customers.subtitle')}</p>
            </div>
          </div>

          {/* Control Bar — always visible, 1 row */}
          {hasAnyData && (
            <div className="px-3 sm:px-6 pb-2.5 sm:pb-4">
              <div className="flex items-center gap-2" data-testid="control-bar">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-zinc-400" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t('customers.searchCustomers') || 'Cari pelanggan...'}
                    className="pl-8 h-9 text-sm"
                    data-testid="search-input"
                  />
                </div>
                {/* Options button — opens drawer on mobile, shows sort info */}
                <button
                  onClick={() => setOptionsDrawerOpen(true)}
                  className="relative p-2 h-9 w-9 flex items-center justify-center rounded-lg border border-slate-200 dark:border-zinc-800 hover:bg-slate-100 dark:hover:bg-zinc-800 text-slate-600 dark:text-zinc-300 transition-colors dark:bg-zinc-800"
                  data-testid="options-btn"
                >
                  <SlidersHorizontal className="w-4 h-4" />
                </button>
              </div>
              {/* Active sort indicator — compact chip */}
              {currentSortValue !== 'last_order-desc' && (
                <div className="flex items-center mt-1.5 sm:hidden">
                  <span className="text-[11px] text-slate-500 flex items-center gap-1 dark:text-zinc-400">
                    <ArrowUpDown className="w-3 h-3" />
                    {currentSortLabel}
                  </span>
                </div>
              )}
              {/* Desktop: inline sort dropdown */}
              <div className="hidden sm:flex items-center gap-2 mt-2">
                <select
                  value={currentSortValue}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split('-');
                    setSortBy(field);
                    setSortOrder(order);
                  }}
                  className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-slate-900 dark:text-zinc-50 text-sm"
                  data-testid="sort-select"
                >
                  {SORT_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { fetchCustomers(); fetchStats(); }}
                  className="text-slate-500 h-8 dark:text-zinc-400"
                  data-testid="refresh-btn"
                >
                  <RefreshCw className="w-3.5 h-3.5 mr-1" />
                  Refresh
                </Button>
              </div>
            </div>
          )}
        </header>

        {/* Main Content */}
        <main className="p-3 sm:p-6">
          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-16 sm:py-24">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          )}
          {/* Empty State — shown immediately, no scroll needed */}
          {!hasAnyData && !loading && (
            <div className="flex flex-col items-center justify-center py-16 sm:py-24 px-4" data-testid="empty-state">
              <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-zinc-900 flex items-center justify-center mb-4">
                <Users className="w-8 h-8 text-slate-500 dark:text-zinc-400" />
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50 mb-1 text-center">
                Belum ada pelanggan
              </h3>
              <p className="text-sm text-slate-500 dark:text-zinc-400 text-center max-w-xs mb-6">
                Data pelanggan akan muncul otomatis dari percakapan dan pesanan yang masuk
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  onClick={() => navigate('/inbox')}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="cta-inbox"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Buka Inbox
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/channels')}
                  data-testid="cta-channels"
                >
                  <Inbox className="w-4 h-4 mr-2" />
                  Hubungkan Channel
                </Button>
              </div>
            </div>
          )}

          {/* Customer List — visible immediately after control bar */}
          {hasAnyData && (
            <>
              {/* Stats — desktop only, above content */}
              {stats && (
                <div className="hidden sm:grid grid-cols-2 md:grid-cols-4 gap-4 mb-4" data-testid="stats-section">
                  <div className="bg-white dark:bg-zinc-900 rounded-xl p-4 border border-slate-200 dark:border-zinc-800">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500 text-sm dark:text-zinc-400">{t('customers.totalCustomers') || 'Total Pelanggan'}</span>
                      <Users className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <p className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50 mt-1">{stats.total_customers}</p>
                  </div>
                  <div className="bg-white dark:bg-zinc-900 rounded-xl p-4 border border-slate-200 dark:border-zinc-800">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500 text-sm dark:text-zinc-400">{t('customers.new') || 'Baru'}</span>
                      <UserPlus className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <p className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50 mt-1">{stats.new_customers}</p>
                    <p className="text-xs text-slate-500 dark:text-zinc-400">{t('customers.thisMonth') || 'Bulan ini'}</p>
                  </div>
                  <div className="bg-white dark:bg-zinc-900 rounded-xl p-4 border border-slate-200 dark:border-zinc-800">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500 text-sm dark:text-zinc-400">{t('customers.repeat') || 'Repeat'}</span>
                      <Repeat className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    </div>
                    <p className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50 mt-1">{stats.repeat_customers}</p>
                    <p className="text-xs text-slate-500 dark:text-zinc-400">&gt;1 order</p>
                  </div>
                  <div className="bg-white dark:bg-zinc-900 rounded-xl p-4 border border-slate-200 dark:border-zinc-800">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-500 text-sm dark:text-zinc-400">Avg. LTV</span>
                      <DollarSign className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    </div>
                    <p className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50 mt-1">{formatCurrency(stats.avg_lifetime_value)}</p>
                  </div>
                </div>
              )}

              {/* Mobile List */}
              <div className="sm:hidden" data-testid="customers-mobile">
                {hasCustomers ? (
                  <div className="divide-y divide-slate-200 dark:divide-zinc-800">
                    {customers.map((customer, index) => (
                      <div
                        key={customer.customer_phone || index}
                        className="flex items-center gap-3 py-3 px-1 active:bg-slate-100 dark:active:bg-slate-800 transition-colors cursor-pointer dark:bg-zinc-800"
                        onClick={() => openCustomerDetail(customer)}
                        data-testid={`customer-mobile-${index}`}
                      >
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold text-sm shrink-0">
                          {customer.customer_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className="font-medium text-slate-900 dark:text-zinc-50 text-sm truncate">
                              {customer.customer_name || 'Pelanggan'}
                            </p>
                            {customer.total_orders > 1 && (
                              <Badge className="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 text-[10px] px-1.5 py-0 shrink-0">
                                Repeat
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-slate-500 truncate dark:text-zinc-400">{customer.customer_phone || '-'}</span>
                            <span className="text-[10px] text-slate-500 dark:text-zinc-400">{customer.total_orders} pesanan</span>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">{formatCurrency(customer.total_spent)}</p>
                          <p className="text-[10px] text-slate-500 dark:text-zinc-400">{formatDate(customer.last_order)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-sm text-slate-500 dark:text-zinc-400" data-testid="no-results">
                    Tidak ada pelanggan yang cocok
                  </div>
                )}
              </div>

              {/* Desktop Table */}
              <div className="hidden sm:block bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="customers-table">
                    <thead className="bg-slate-50 dark:bg-zinc-800/50 sticky top-0 z-10">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider dark:text-zinc-400">Pelanggan</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider dark:text-zinc-400">Kontak</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 dark:text-zinc-200"
                          onClick={() => { setSortBy(sortBy === 'total_orders' && sortOrder === 'desc' ? 'total_orders' : 'total_orders'); setSortOrder(sortBy === 'total_orders' && sortOrder === 'desc' ? 'asc' : 'desc'); }}>
                          <span className="flex items-center gap-1">Pesanan <ArrowUpDown className="w-3 h-3" /></span>
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 dark:text-zinc-200"
                          onClick={() => { setSortBy(sortBy === 'total_spent' && sortOrder === 'desc' ? 'total_spent' : 'total_spent'); setSortOrder(sortBy === 'total_spent' && sortOrder === 'desc' ? 'asc' : 'desc'); }}>
                          <span className="flex items-center gap-1">Total Belanja <ArrowUpDown className="w-3 h-3" /></span>
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 dark:text-zinc-200"
                          onClick={() => { setSortBy(sortBy === 'last_order' && sortOrder === 'desc' ? 'last_order' : 'last_order'); setSortOrder(sortBy === 'last_order' && sortOrder === 'desc' ? 'asc' : 'desc'); }}>
                          <span className="flex items-center gap-1">Terakhir Order <ArrowUpDown className="w-3 h-3" /></span>
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider dark:text-zinc-400">Aksi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-zinc-800">
                      {customers.map((customer, index) => (
                        <tr key={customer.customer_phone || index} className="hover:bg-slate-50 dark:hover:bg-zinc-800/50 transition-colors dark:bg-zinc-900" data-testid={`customer-row-${index}`}>
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold text-sm">
                                {customer.customer_name?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                              <div>
                                <p className="font-medium text-slate-900 dark:text-zinc-50">{customer.customer_name || 'Pelanggan'}</p>
                                {customer.total_orders > 1 && (
                                  <Badge className="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 text-xs mt-0.5">
                                    <Repeat className="w-3 h-3 mr-1" />Repeat
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <p className="text-sm text-slate-900 dark:text-zinc-50">{customer.customer_phone || '-'}</p>
                            {customer.address && customer.address !== '-' && (
                              <p className="text-xs text-slate-500 truncate max-w-[150px] dark:text-zinc-400">{customer.address}</p>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <span className="font-semibold text-slate-900 dark:text-zinc-50">{customer.total_orders}</span>
                            <span className="text-slate-500 text-sm dark:text-zinc-400"> pesanan</span>
                          </td>
                          <td className="px-4 py-4">
                            <p className="font-semibold text-indigo-600 dark:text-indigo-400">{formatCurrency(customer.total_spent)}</p>
                            <p className="text-xs text-slate-500 dark:text-zinc-400">Avg: {formatCurrency(customer.avg_order_value)}</p>
                          </td>
                          <td className="px-4 py-4 text-sm text-slate-500 dark:text-zinc-400">{formatDate(customer.last_order)}</td>
                          <td className="px-4 py-4">
                            <Button size="sm" variant="outline" onClick={() => openCustomerDetail(customer)} data-testid={`view-customer-${index}`}>
                              <Eye className="w-4 h-4 mr-1" />Detail
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {customers.length === 0 && (
                        <tr>
                          <td colSpan={6} className="px-4 py-12 text-center text-slate-500 dark:text-zinc-400">Tidak ada pelanggan yang cocok</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              <div className="mt-3 sm:mt-4">
                <Pagination
                  pagination={pagination}
                  onPageChange={goToPage}
                  itemLabel="pelanggan"
                />
              </div>
            </>
          )}
        </main>
      </div>

      {/* Options Drawer — Sort + Refresh (mobile) */}
      <Drawer open={optionsDrawerOpen} onOpenChange={setOptionsDrawerOpen}>
        <DrawerContent className="bg-white dark:bg-zinc-900">
          <DrawerHeader className="pb-2">
            <DrawerTitle className="text-base font-semibold text-slate-900 dark:text-zinc-50 flex items-center gap-2">
              <SlidersHorizontal className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              Urutkan & Opsi
            </DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-4 space-y-1" data-testid="options-drawer-content">
            {SORT_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => {
                  const [field, order] = opt.value.split('-');
                  setSortBy(field);
                  setSortOrder(order);
                  setOptionsDrawerOpen(false);
                }}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  currentSortValue === opt.value
                    ? 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 font-medium'
                    : 'text-slate-700 dark:text-zinc-300 hover:bg-slate-100 dark:hover:bg-zinc-800'
                }`}
                data-testid={`sort-${opt.value}`}
              >
                <span>{opt.label}</span>
                {currentSortValue === opt.value && <Check className="w-4 h-4" />}
              </button>
            ))}
          </div>
          <div className="px-4 pb-6 pt-2 border-t border-slate-200 dark:border-zinc-800">
            <button
              onClick={() => { fetchCustomers(); fetchStats(); setOptionsDrawerOpen(false); toast.success('Data diperbarui'); }}
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm text-slate-700 dark:text-zinc-300 hover:bg-slate-100 dark:hover:bg-zinc-800 transition-colors dark:bg-zinc-800"
              data-testid="drawer-refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh Data
            </button>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Customer Detail Modal */}
      <CustomerDetailModal
        customer={selectedCustomer}
        isOpen={showDetail}
        onClose={() => setShowDetail(false)}
      />
    </div>
  );
}
